import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { useVerifyEmailMutation } from "@/store/api";
import { useAppDispatch, useAppSelector } from "@/store";
import { setSession } from "@/store/authSlice";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authed = useAppSelector((s) => s.auth.token);
  const [verify] = useVerifyEmailMutation();
  const [state, setState] = useState<"verifying" | "ok" | "error">(
    token ? "verifying" : "error",
  );
  const [error, setError] = useState<string | null>(
    token ? null : "This link is missing the verification token.",
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await verify({ token }).unwrap();
        if (cancelled) return;
        dispatch(setSession({ token: res.token, user: res.user }));
        setState("ok");
        toast.success("Email verified. Welcome!");
        setTimeout(() => navigate("/settings", { replace: true }), 800);
      } catch (e) {
        if (cancelled) return;
        const msg = (e as { data?: { detail?: string } })?.data?.detail
          ?? "We couldn't verify this link.";
        setError(msg);
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, verify, dispatch, navigate]);

  if (authed && state === "ok") return <Navigate to="/settings" replace />;

  return (
    <div className="min-h-screen grid place-items-center bg-bg text-fg p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>
            {state === "verifying" && "Verifying your email…"}
            {state === "ok" && "Email verified"}
            {state === "error" && "Verification problem"}
          </CardTitle>
          <CardDescription>
            {state === "verifying" && "One moment while we confirm your link."}
            {state === "ok" && "Signing you in — taking you to Settings."}
            {state === "error" && "The link is invalid or has expired."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {state === "error" && error && <Alert tone="danger">{error}</Alert>}
          {state === "error" && (
            <>
              <p className="text-xs text-muted">
                Verification links expire after 24 hours. You can request a
                fresh one from the sign-in screen.
              </p>
              <Link to="/login">
                <Button variant="primary" size="sm" className="w-full">
                  Go to sign in
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
